import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function NanoHealSpray() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Safe translation extraction for arrays and objects
  const targetAnimals = t("nanoHealSprayPage.species.items", { returnObjects: true }) || [];
  const indications = t("nanoHealSprayPage.indications.items", { returnObjects: true }) || [];
  const mechanismSteps = t("nanoHealSprayPage.mechanism.steps", { returnObjects: true }) || [];
  const comparisonRows = t("nanoHealSprayPage.comparison.rows", { returnObjects: true }) || [];
  const usageSteps = t("nanoHealSprayPage.usage.steps", { returnObjects: true }) || [];
  const safetyItems = t("nanoHealSprayPage.safety.items", { returnObjects: true }) || [];
  const targetSectors = t("nanoHealSprayPage.sectors.items", { returnObjects: true }) || [];

  // Pillars extraction for the "Why different" section
  const pillar1Benefits = t("nanoHealSprayPage.different.pillars.pillar1.benefits", { returnObjects: true }) || [];
  const pillar2Benefits = t("nanoHealSprayPage.different.pillars.pillar2.benefits", { returnObjects: true }) || [];
  const pillar3Benefits = t("nanoHealSprayPage.different.pillars.pillar3.benefits", { returnObjects: true }) || [];

  return (
    <div style={{ fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif', color: "#2d3748", backgroundColor: "#f7fafc", paddingBottom: "80px" }}>
      
      {/* HERO HEADER */}
      <section style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)", color: "#ffffff", padding: "120px 20px 70px", textAlign: "center" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <span style={{ background: "#2563eb", color: "#ffffff", padding: "6px 16px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: "700", letterSpacing: "1px", textTransform: "uppercase" }}>
            {t("nanoHealSprayPage.hero.tag")}
          </span>
          <h1 style={{ fontSize: "3rem", margin: "20px 0 10px", fontWeight: "800", color: "#ffffff" }}>
            {t("nanoHealSprayPage.hero.title")}
          </h1>
          <p style={{ fontSize: "1.25rem", opacity: "0.9", maxWidth: "700px", margin: "0 auto 35px", lineHeight: "1.5" }}>
            {t("nanoHealSprayPage.hero.description")}
          </p>
          <button 
            onClick={() => navigate("/products")}
            style={{ padding: "14px 35px", fontSize: "1.1rem", background: "#3b82f6", color: "#ffffff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", boxShadow: "0 4px 14px rgba(59, 130, 246, 0.4)", transition: "background 0.2s" }}
            onMouseEnter={(e) => e.target.style.background = "#2563eb"}
            onMouseLeave={(e) => e.target.style.background = "#3b82f6"}
          >
            {t("nanoHealSprayPage.hero.orderBtn")}
          </button>
        </div>
      </section>

      {/* INTRODUCTION CONTAINER */}
      <section style={{ maxWidth: "1000px", margin: "-30px auto 50px", padding: "40px", background: "#ffffff", borderRadius: "12px", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}>
        <h2 style={{ color: "#1e3a8a", borderBottom: "2px solid #edf2f7", paddingBottom: "12px", marginTop: 0 }}>
          {t("nanoHealSprayPage.intro.title")}
        </h2>
        <p style={{ lineHeight: "1.7", color: "#4a5568" }}>
          {t("nanoHealSprayPage.intro.p1")}
        </p>
        <p style={{ lineHeight: "1.7", color: "#4a5568", marginTop: "15px" }}>
          {t("nanoHealSprayPage.intro.p2")}
        </p>
        <p style={{ lineHeight: "1.7", color: "#4a5568", marginTop: "15px" }}>
          {t("nanoHealSprayPage.intro.p3")}
        </p>
        <p style={{ lineHeight: "1.7", color: "#4a5568", marginTop: "15px", fontWeight: "500" }}>
          {t("nanoHealSprayPage.intro.p4")}
        </p>
      </section>

      {/* WHY NANO HEAL SPRAY IS DIFFERENT */}
      <section style={{ maxWidth: "1000px", margin: "0 auto 50px", padding: "0 20px" }}>
        <div style={{ textAlign: "center", marginBottom: "35px" }}>
          <h2 style={{ color: "#1e3a8a", fontSize: "2rem" }}>
            {t("nanoHealSprayPage.different.title")}
          </h2>
          <p style={{ color: "#718096", maxWidth: "750px", margin: "10px auto 0", lineHeight: "1.6" }}>
            {t("nanoHealSprayPage.different.description")}
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "25px" }}>
          {/* Pillar 1 */}
          <div style={{ background: "#ffffff", padding: "30px", borderRadius: "10px", boxShadow: "0 4px 15px rgba(0,0,0,0.02)", border: "1px solid #e2e8f0" }}>
            <h3 style={{ color: "#2563eb", fontSize: "1.25rem", marginTop: 0 }}>
              {t("nanoHealSprayPage.different.pillars.pillar1.title")}
            </h3>
            <p style={{ color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
              {t("nanoHealSprayPage.different.pillars.pillar1.desc")}
            </p>
            <h4 style={{ color: "#1e3a8a", fontSize: "0.9rem", margin: "15px 0 5px 0" }}>
              {t("nanoHealSprayPage.different.pillars.pillar1.subTitle")}
            </h4>
            <ul style={{ paddingLeft: "20px", margin: 0, color: "#4a5568", fontSize: "0.9rem", lineHeight: "1.6" }}>
              {pillar1Benefits.map((benefit, i) => (
                <li key={i}>{benefit}</li>
              ))}
            </ul>
          </div>

          {/* Pillar 2 */}
          <div style={{ background: "#ffffff", padding: "30px", borderRadius: "10px", boxShadow: "0 4px 15px rgba(0,0,0,0.02)", border: "1px solid #e2e8f0" }}>
            <h3 style={{ color: "#2563eb", fontSize: "1.25rem", marginTop: 0 }}>
              {t("nanoHealSprayPage.different.pillars.pillar2.title")}
            </h3>
            <p style={{ color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
              {t("nanoHealSprayPage.different.pillars.pillar2.desc")}
            </p>
            <h4 style={{ color: "#1e3a8a", fontSize: "0.9rem", margin: "15px 0 5px 0" }}>
              {t("nanoHealSprayPage.different.pillars.pillar2.subTitle")}
            </h4>
            <ul style={{ paddingLeft: "20px", margin: 0, color: "#4a5568", fontSize: "0.9rem", lineHeight: "1.6" }}>
              {pillar2Benefits.map((benefit, i) => (
                <li key={i}>{benefit}</li>
              ))}
            </ul>
          </div>

          {/* Pillar 3 */}
          <div style={{ background: "#ffffff", padding: "30px", borderRadius: "10px", boxShadow: "0 4px 15px rgba(0,0,0,0.02)", border: "1px solid #e2e8f0" }}>
            <h3 style={{ color: "#2563eb", fontSize: "1.25rem", marginTop: 0 }}>
              {t("nanoHealSprayPage.different.pillars.pillar3.title")}
            </h3>
            <p style={{ color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
              {t("nanoHealSprayPage.different.pillars.pillar3.desc")}
            </p>
            <h4 style={{ color: "#1e3a8a", fontSize: "0.9rem", margin: "15px 0 5px 0" }}>
              {t("nanoHealSprayPage.different.pillars.pillar3.subTitle")}
            </h4>
            <ul style={{ paddingLeft: "20px", margin: 0, color: "#4a5568", fontSize: "0.9rem", lineHeight: "1.6" }}>
              {pillar3Benefits.map((benefit, i) => (
                <li key={i}>{benefit}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* KEY BENEFITS SECTION */}
      <section style={{ backgroundColor: "#edf2f7", padding: "60px 20px" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <h2 style={{ textAlign: "center", color: "#1e3a8a", fontSize: "2rem", marginBottom: "40px" }}>
            {t("nanoHealSprayPage.benefits.title")}
          </h2>
          
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))", gap: "25px" }}>
            
            <div style={{ background: "#ffffff", padding: "25px", borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#1e3a8a", fontSize: "1.1rem" }}>
                {t("nanoHealSprayPage.benefits.items.healing.title")}
              </h4>
              <p style={{ margin: 0, color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
                {t("nanoHealSprayPage.benefits.items.healing.desc")}
              </p>
            </div>

            <div style={{ background: "#ffffff", padding: "25px", borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#1e3a8a", fontSize: "1.1rem" }}>
                {t("nanoHealSprayPage.benefits.items.infection.title")}
              </h4>
              <p style={{ margin: 0, color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
                {t("nanoHealSprayPage.benefits.items.infection.desc")}
              </p>
            </div>

            <div style={{ background: "#ffffff", padding: "25px", borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#1e3a8a", fontSize: "1.1rem" }}>
                {t("nanoHealSprayPage.benefits.items.maggot.title")}
              </h4>
              <p style={{ margin: 0, color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
                {t("nanoHealSprayPage.benefits.items.maggot.desc")}
              </p>
            </div>

            <div style={{ background: "#ffffff", padding: "25px", borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#1e3a8a", fontSize: "1.1rem" }}>
                {t("nanoHealSprayPage.benefits.items.inflammatory.title")}
              </h4>
              <p style={{ margin: 0, color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
                {t("nanoHealSprayPage.benefits.items.inflammatory.desc")}
              </p>
            </div>

          </div>

          {/* BIOFILM CALLOUT CARD */}
          <div style={{ background: "#ebf8ff", borderLeft: "4px solid #3182ce", padding: "20px", borderRadius: "4px", marginTop: "30px" }}>
            <h4 style={{ margin: "0 0 5px 0", color: "#2b6cb0", fontSize: "1.05rem" }}>
              {t("nanoHealSprayPage.benefits.biofilm.title")}
            </h4>
            <p style={{ margin: 0, color: "#2d3748", fontSize: "0.95rem", lineHeight: "1.6" }}>
              {t("nanoHealSprayPage.benefits.biofilm.desc")}
            </p>
          </div>

        </div>
      </section>

      {/* INDICATIONS & TARGET ANIMALS */}
      <section style={{ maxWidth: "1000px", margin: "60px auto", padding: "0 20px" }}>
        <h2 style={{ textAlign: "center", color: "#1e3a8a", marginBottom: "35px" }}>
          {t("nanoHealSprayPage.indications.title")}
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
          {indications.map((ind, idx) => (
            <div key={idx} style={{ background: "#ffffff", padding: "20px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <h4 style={{ margin: "0 0 8px 0", color: "#2b6cb0" }}>{ind.title}</h4>
              <p style={{ margin: 0, fontSize: "0.9rem", color: "#4a5568", lineHeight: "1.5" }}>{ind.desc}</p>
            </div>
          ))}
        </div>

        {/* SPECIES GRID */}
        <div style={{ background: "#ffffff", padding: "30px", borderRadius: "10px", marginTop: "40px", border: "1px solid #e2e8f0", textAlign: "center" }}>
          <h3 style={{ color: "#1e3a8a", marginTop: 0, marginBottom: "15px" }}>
            {t("nanoHealSprayPage.species.title")}
          </h3>
          <p style={{ color: "#4a5568", marginBottom: "20px" }}>
            {t("nanoHealSprayPage.species.intro")}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center" }}>
            {targetAnimals.map((animal, i) => (
              <span key={i} style={{ background: "#edf2f7", color: "#2b6cb0", padding: "8px 18px", borderRadius: "20px", fontSize: "0.9rem", fontWeight: "600" }}>
                {animal}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* MECHANISM OF ACTION */}
      <section style={{ backgroundColor: "#f7fafc", padding: "5px 20px", maxWidth: "900px", margin: "0 auto" }}>
        <h2 style={{ textAlign: "center", color: "#1e3a8a", marginBottom: "35px" }}>
          {t("nanoHealSprayPage.mechanism.title")}
        </h2>
        <div style={{ background: "#ffffff", padding: "35px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
          <p style={{ margin: "0 0 25px 0", color: "#4a5568" }}>
            {t("nanoHealSprayPage.mechanism.intro")}
          </p>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {mechanismSteps.map((step, idx) => (
              <div key={idx} style={{ display: "flex", gap: "15px" }}>
                <div style={{ background: "#1e3a8a", color: "white", width: "28px", height: "28px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", flexShrink: 0 }}>
                  {step.id}
                </div>
                <div>
                  <strong style={{ color: "#1e3a8a" }}>{step.label}:</strong> {step.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARATIVE ANALYSIS TABLE */}
      <section style={{ maxWidth: "1000px", margin: "60px auto", padding: "0 20px" }}>
        <h2 style={{ textAlign: "center", color: "#1e3a8a", marginBottom: "30px" }}>
          {t("nanoHealSprayPage.comparison.title")}
        </h2>
        <div style={{ overflowX: "auto", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#ffffff", textAlign: "left", minWidth: "500px" }}>
            <thead>
              <tr style={{ backgroundColor: "#1e3a8a", color: "white" }}>
                <th style={{ padding: "15px 20px" }}>{t("nanoHealSprayPage.comparison.headers.feature")}</th>
                <th style={{ padding: "15px 20px" }}>{t("nanoHealSprayPage.comparison.headers.conventional")}</th>
                <th style={{ padding: "15px 20px", backgroundColor: "#2b6cb0" }}>{t("nanoHealSprayPage.comparison.headers.nanoHeal")}</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, idx) => (
                <tr key={idx} style={{ borderBottom: idx === comparisonRows.length - 1 ? "none" : "1px solid #edf2f7" }}>
                  <td style={{ padding: "15px 20px", fontWeight: "600" }}>{row.feature}</td>
                  <td style={{ padding: "15px 20px", color: "#718096" }}>{row.conventional}</td>
                  <td style={{ padding: "15px 20px", fontWeight: "bold", color: "#2b6cb0", backgroundColor: idx === comparisonRows.length - 1 ? "#f0f4f8" : "transparent" }}>
                    {row.nanoHeal}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* APPLICATION MANUAL, DOSAGE, AND SAFETY PRECAUTIONS */}
      <section style={{ maxWidth: "1000px", margin: "0 auto 40px", padding: "0 20px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "30px" }}>
        
        {/* DIRECTIONS FOR USE */}
        <div style={{ background: "#ffffff", padding: "30px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
          <h3 style={{ color: "#1e3a8a", marginTop: 0, marginBottom: "15px" }}>
            {t("nanoHealSprayPage.usage.title")}
          </h3>
          <h4 style={{ margin: "0 0 10px 0", color: "#718096", fontSize: "0.95rem" }}>
            {t("nanoHealSprayPage.usage.subTitle")}
          </h4>
          <ol style={{ paddingLeft: "20px", color: "#4a5568", lineHeight: "1.8", margin: 0 }}>
            {usageSteps.map((step, idx) => (
              <li key={idx}>{step}</li>
            ))}
          </ol>

          <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px dashed #e2e8f0" }}>
            <strong style={{ color: "#1e3a8a", display: "block", marginBottom: "5px" }}>
              {t("nanoHealSprayPage.usage.dosageTitle")}
            </strong>
            <p style={{ margin: 0, color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
              {t("nanoHealSprayPage.usage.dosageDesc")}
            </p>
          </div>
        </div>

        {/* SAFETY PRECAUTIONS */}
        <div style={{ background: "#fff5f5", border: "1px solid #fed7d7", padding: "30px", borderRadius: "10px" }}>
          <h3 style={{ color: "#9b2c2c", marginTop: 0, marginBottom: "15px" }}>
            {t("nanoHealSprayPage.safety.title")}
          </h3>
          <ul style={{ paddingLeft: "20px", color: "#9b2c2c", lineHeight: "1.8", margin: 0 }}>
            {safetyItems.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>

      </section>

      {/* COMMERCIAL TARGET SECTORS */}
      <section style={{ maxWidth: "1000px", margin: "0 auto 50px", padding: "0 20px" }}>
        <div style={{ background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)", padding: "35px 40px", borderRadius: "12px", color: "#ffffff", textAlign: "center" }}>
          <h3 style={{ color: "#60a5fa", margin: "0 0 10px 0", fontSize: "1.30rem" }}>
            {t("nanoHealSprayPage.sectors.title")}
          </h3>
          <p style={{ color: "#94a3b8", fontSize: "0.95rem", maxWidth: "700px", margin: "0 auto 25px auto", lineHeight: "1.5" }}>
            {t("nanoHealSprayPage.sectors.subtitle")}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center" }}>
            {targetSectors.map((sector, index) => (
              <span key={index} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", color: "#f8fafc", padding: "8px 16px", borderRadius: "6px", fontSize: "0.85rem", fontWeight: "500" }}>
                {sector}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* OUTRO CONCLUSION */}
      <section style={{ maxWidth: "800px", margin: "0 auto", padding: "0 20px", textAlign: "center" }}>
        <h2 style={{ color: "#1e3a8a", fontSize: "1.75rem", marginBottom: "15px" }}>
          {t("nanoHealSprayPage.conclusion.title")}
        </h2>
        <p style={{ color: "#4a5568", lineHeight: "1.7", fontSize: "1rem" }}>
          {t("nanoHealSprayPage.conclusion.p1")}
        </p>
        <p style={{ color: "#4a5568", lineHeight: "1.7", fontSize: "1rem", marginTop: "15px" }}>
          {t("nanoHealSprayPage.conclusion.p2")}
        </p>
        <button 
          onClick={() => navigate("/products")}
          style={{ marginTop: "30px", padding: "14px 40px", fontSize: "1.1rem", background: "#2b6cb0", color: "#ffffff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", boxShadow: "0 4px 12px rgba(43,108,176,0.3)" }}
        >
          {t("nanoHealSprayPage.conclusion.returnBtn")}
        </button>
      </section>

    </div>
  );
}

export default NanoHealSpray;